// src/components/imageInputContextMenuUtils.ts
import { KernelHandle, Layer, LockableFile, QuickMenuAction } from '#types/types';
import { v4 as uuidv4 } from 'uuid';
import { loadImageDataFromUrl } from '#lib/canvas-utils';
import { imageDataToFile } from '#lib/utils';
import {
  getProjectFocusedListOutputId,
  listProjectLayers,
  listProjectWorkspaces,
  resolveFocusedListOutput,
  resolveLatestListOutput,
} from '@/lib/studio-list-focus';

export interface ContextMenuActionDependencies {
  onImagesChange: (files: LockableFile[]) => void;
  imageFiles?: LockableFile[];
  imageSrc?: string | null;
  layers: Layer[];
  selectedLayersId: string[];
  multiple: boolean; // To decide how to add new files
  kernel: React.RefObject<KernelHandle | null>;
  workspaceId: string;
  /** Project kind. LIST has no canvas, so it gets an output-based attach action instead. */
  projectType?: string;
}

/**
 * LIST has no Generation Area: its workspace is a flat list of finished outputs, so the
 * equivalent of "attach what I am looking at" is literally the output open in the viewer, and
 * the newest delivered output only when nothing is open. Skips versions that carry no image, and
 * drafts the user never chose.
 *
 * "Active" MUST mean the same image here as it does for the agent (see lib/studio-list-focus.ts):
 * resolving it to "newest" while the user was looking at an older output attached the wrong one.
 * Both readings therefore share one resolver, one draft predicate, and the same
 * every-workspace-of-the-project scope as the grid the user clicked in.
 */
const resolveActiveListOutputUrl = (
  kernel: React.RefObject<KernelHandle | null>,
  workspaceId: string,
): string | null => {
  const handle = kernel.current;
  const workspace = handle?.getWorkspace(workspaceId);
  if (!handle || !workspace) return null;
  const focused = resolveFocusedListOutput(
    listProjectWorkspaces(handle, workspace.projectId),
    getProjectFocusedListOutputId(handle, workspace.projectId),
    listProjectLayers(handle, workspace.projectId),
  );
  if (focused) return focused.imageUrl;
  return resolveLatestListOutput(
    listProjectWorkspaces(handle, workspace.projectId),
    listProjectLayers(handle, workspace.projectId),
  )?.imageUrl ?? null;
};

export const getGenericImageInputContextMenuActions = (
  deps: ContextMenuActionDependencies
): QuickMenuAction[] => {
  const {
    onImagesChange,
    imageFiles,
    imageSrc,
    layers,
    selectedLayersId,
    multiple,
    workspaceId
  } = deps;

  const actions: QuickMenuAction[] = [];

  actions.push({
    label: 'Clear All',
    onClick: () => { onImagesChange([]); },
    selected: (imageFiles && imageFiles.length > 0) || imageSrc != null,
  });

  if (deps.kernel && workspaceId.trim().length > 0) {
    const isList = deps.projectType === 'LIST';
    const attach = (file: File) => {
      const lockableFile: LockableFile = { id: uuidv4(), file, locked: false };
      onImagesChange(multiple ? [...(imageFiles || []), lockableFile] : [lockableFile]);
    };
    // LIST has no Generation Area to render, so it offers its own outputs instead. Naming it
    // "Add Generation Area" there pointed at a concept the module does not have, and rendering
    // the workspace area on a flat output list is meaningless.
    actions.push(
      isList
        ? {
            label: 'Add active output',
            onClick: async () => {
              const url = resolveActiveListOutputUrl(deps.kernel, workspaceId);
              if (!url) return;
              try {
                const imageData = await loadImageDataFromUrl(url);
                const file = await imageDataToFile(imageData, 'active-output.webp', 'image/webp');
                if (file) attach(file);
              } catch (error) {
                console.error('Could not attach the active List output.', error);
                deps.kernel.current?.handleError('Could not attach the active output.');
              }
            },
          }
        : {
            label: 'Add Generation Area',
            onClick: async () => {
              const originalImage = await deps.kernel.current?.renderWorkspaceArea(workspaceId, undefined, 2000000);
              if (originalImage && originalImage.file) attach(originalImage.file);
            },
          },
    );
  }

  if (selectedLayersId.length > 0 && layers.length > 0) {
    actions.push({
      label: 'Add selected layers',
      onClick: () => {
        const filesToAddPromises: Promise<File | null>[] = selectedLayersId.map(layerId =>
          imageDataToFile(
            layers.find(layer => layer.id === layerId)?.imageData ?? null,
            `layer-${layerId}.webp`,
            'image/webp',
          ),
        );

        Promise.all(filesToAddPromises).then((resolvedFiles) => {
          const validFiles = resolvedFiles.filter((file): file is File => file !== null);
          if (validFiles.length > 0) {
            const lockableFiles: LockableFile[] = validFiles.map(f => ({ id: uuidv4(), file: f, locked: false }));
            if (multiple) {
              onImagesChange([...(imageFiles || []), ...lockableFiles]);
            } else {
              const firstFile = lockableFiles[0];
              if (firstFile) {
                onImagesChange([firstFile]);
              }
            }
          }
        });
      }
    });
  }

  return actions.filter(action => action.selected === undefined || action.selected);
};
